import * as Icons from "heroicons-react";
import { signInUser } from "../../Utility/Auth";
import { useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import { useEffect, useState } from "react";
import { format } from "date-fns";
import WareHouseDW from "../../Common/CustomSelect/WareHouseDW";
import SelectCustomerReport from "../../Common/CustomSelect/SelectCustomerReport";
import { useWarehouseQuery } from "../../../services/warehouseApi";
import { useSaleReturnWiseQuery } from "../../../services/reportsApi";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const CustomerWiseSalesReturnReport = () => {
  const auth = signInUser();

  const [warehouse, setWarehouse] = useState("allWh");
  const [customerId, setCustomerId] = useState();

  const [startDate, setStartDate] = useState(format(new Date(), "MM-dd-yyyy"));
  const [endDate, setEndDate] = useState(format(new Date(), "MM-dd-yyyy"));
  const { aamarId } = auth || {};
  const lang = useSelector((state) => state.languageReducer);

  const {
    data: returnData,
    isLoading,
    isError,
    error,
  } = useSaleReturnWiseQuery({
    startDate,
    endDate,
    warehouse,
    aamarId,
    customerId,
  });

  const [whName, setWhName] = useState("All Warehouses");
  const { data: wh, refetch } = useWarehouseQuery(warehouse);

  useEffect(() => {
    if (warehouse) {
      refetch();
    }
  }, [warehouse, refetch]);

  useEffect(() => {
    if (wh?.name) {
      setWhName(wh.name);
    }
  }, [wh]);

  // Utility to sum values
  const sumField = (data, field) =>
    (data || [])
      .reduce((acc, item) => acc + (parseFloat(item?.[field]) || 0), 0)
      .toFixed(2);

  // Export CSV
  const handleExportCSV = () => {
    if (!returnData || returnData.length === 0) {
      alert("No data available for export.");
      return;
    }

    const start = new Date(startDate).toLocaleDateString("en-US");
    const end = new Date(endDate).toLocaleDateString("en-US");
    const generatedOn = new Date().toLocaleString();
    const generatedBy = auth?.name || "Aamar Dokan";

    const storeName = auth?.storeSettings?.storeName?.toUpperCase() || "Aamar Dokan";
    const warehouseName = whName;
    const reportTitle = "Customer Wise Sales Return Report";

    const headerInfo = [
      `,Store: ${storeName},,,,,Warehouse: ${warehouseName}`,
      `,Report: ${reportTitle},,,,,Date Range: ${start} to ${end}`,
      `,Generated On: ${generatedOn},,,,Generated By: ${generatedBy}`,
      "",
      "",
    ];

    const csvTableHeader = [
      "S/N",
      "Invoice No",
      "Date",
      "Customer Name",
      "Phone",
      "Warehouse",
      "Total Items",
      "Total Return",
      "Gross Total",
    ].join(",");

    let totalItems = 0;
    let totalReturnAmount = 0;
    let totalGrossAmount = 0;

    const csvRows = returnData.map((item, index) => {
      const items = Number(item?.totalItem || 0);
      const returnAmt = Number(item?.total || 0);
      const grossAmt = Number(item?.grossTotal || 0);

      totalItems += items;
      totalReturnAmount += returnAmt;
      totalGrossAmount += grossAmt;

      const dateStr = item?.date ? new Date(item.date).toLocaleDateString("en-US") : "N/A";
      const customer = item?.customer || "Walkway Customer";
      const phone = item?.phone || "N/A";
      const whStr = item?.warehouse || whName;

      return [
        index + 1,
        `"${item?.invoiceId || ""}"`,
        `"${dateStr}"`,
        `"${customer}"`,
        `"${phone}"`,
        `"${whStr}"`,
        items,
        returnAmt.toFixed(2),
        grossAmt.toFixed(2),
      ].join(",");
    });

    const summaryLabelRow = ["", "", "", "", "", "", "", "** TOTAL SUMMARY **"].join(",");
    const summaryHeaderRow = ["", "", "", "", "", "Total Items", "Total Return", "Gross Total"].join(",");
    const summaryDataRow = [
      "",
      "",
      "",
      "",
      "",
      totalItems,
      totalReturnAmount.toFixed(2),
      totalGrossAmount.toFixed(2),
    ].join(",");

    const csvContent = [
      ...headerInfo,
      csvTableHeader,
      ...csvRows,
      "",
      "",
      summaryLabelRow,
      summaryHeaderRow,
      summaryDataRow,
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "CustomerWiseSalesReturnReport.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Export PDF
  const handleExportPDF = () => {
    const doc = new jsPDF("landscape");

    if (!returnData || returnData.length === 0) {
      alert("No return data found.");
      return;
    }

    const options = { month: "numeric", day: "numeric", year: "numeric" };
    const formattedStart = new Date(startDate).toLocaleDateString("en-US", options);
    const formattedEnd = new Date(endDate).toLocaleDateString("en-US", options);
    const reportGeneratedAt = new Date().toLocaleString();

    const storeName = auth?.storeSettings?.storeName?.toUpperCase() || "Aamar Dokan";
    const warehouseName = whName;
    const reportName = "Customer Wise Sales Return Report";

    // Title
    doc.setFontSize(16);
    doc.text(storeName, 140, 15, { align: "center" });

    doc.setFontSize(12);
    doc.text(reportName, 140, 22, { align: "center" });

    // Header info
    doc.setFontSize(10);
    doc.text(`Store: ${storeName}`, 14, 28);
    doc.text(`Warehouse: ${warehouseName}`, 14, 33);
    doc.text(`Date: ${formattedStart} - ${formattedEnd}`, 14, 39);
    doc.text(`Generated At: ${reportGeneratedAt}`, 280, 28, { align: "right" });
    doc.text(`Generated By: ${auth?.name || "Aamar Dokan"}`, 280, 33, { align: "right" });

    // Table headers
    const headers = [
      [
        "S/N",
        "Invoice No",
        "Date",
        "Customer Name",
        "Phone",
        "Warehouse",
        "Total Items",
        "Total Return",
        "Gross Total",
      ],
    ];

    const body = returnData.map((item, index) => [
      index + 1,
      item?.invoiceId || "N/A",
      item?.date ? new Date(item.date).toLocaleDateString("en-US") : "N/A",
      item?.customer || "Walkway Customer",
      item?.phone || "N/A",
      item?.warehouse || whName,
      Number(item?.totalItem || 0),
      Number(item?.total || 0).toFixed(2),
      Number(item?.grossTotal || 0).toFixed(2),
    ]);

    autoTable(doc, {
      head: headers,
      body: body,
      startY: 45,
      theme: "grid",
      styles: {
        fontSize: 9,
        halign: "center",
      },
      headStyles: {
        fillColor: [242, 101, 45],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
    });

    // Summary Box
    const finalY = doc.lastAutoTable.finalY + 10;
    const boxWidth = 80;
    const pageWidth = doc.internal.pageSize.getWidth();
    const boxX = pageWidth - boxWidth - 15;
    const boxY = finalY;

    doc.setLineDashPattern([1, 1], 0);
    doc.rect(boxX, boxY, boxWidth, 30);
    doc.setLineDashPattern([], 0);

    const labelX = boxX + 5;
    const valueX = boxX + boxWidth - 5;
    let y = boxY + 10;

    doc.setFontSize(9);

    const totalItems = sumField(returnData, "totalItem");
    const totalReturn = sumField(returnData, "total");
    const totalGross = sumField(returnData, "grossTotal");

    doc.text("Total Items:", labelX, y);
    doc.text(`${totalItems}`, valueX, y, { align: "right" });

    y += 6;
    doc.text("Total Return:", labelX, y);
    doc.text(`${totalReturn}`, valueX, y, { align: "right" });

    y += 6;
    doc.text("Gross Total:", labelX, y);
    doc.text(`${totalGross}`, valueX, y, { align: "right" });

    // Save PDF
    doc.save("CustomerWiseSalesReturnReport.pdf");
  };

  const handleOnchangeWareHouseFrom = (e) => {
    if (e.option !== "no-warehouse") {
      setWarehouse(e.option);
    } else {
      setWarehouse("allWh");
    }
  };

  return (
    <div>
      <div>
        <h2 className="mb-3">
          <Icons.ShoppingCartOutline size={35} /> Customer Wise Sales Return Report
        </h2>
      </div>

      <div
        className="mt-3 w-40 p-3"
        style={{ backgroundColor: "#f1f3f5", borderRadius: "8px" }}
      >
        <div className="d-flex mt-2 mb-2 align-items-center justify-content-center gap-2">
          <DatePicker
            selected={new Date(startDate)}
            className="form-control w-100"
            onChange={(date) =>
              setStartDate(format(new Date(date), "MM-dd-yyyy"))
            }
          />
          <DatePicker
            selected={new Date(endDate)}
            className="form-control me-4"
            onChange={(date) =>
              setEndDate(format(new Date(date), "MM-dd-yyyy"))
            }
          />
        </div>

        <div>
          {auth?.type === "admin" && (
            <div className="my-3">
              <WareHouseDW
                id="warehouse"
                name="warehouse"
                handleOnChange={handleOnchangeWareHouseFrom}
                className="form-control"
              />
            </div>
          )}
          <SelectCustomerReport
            aamarId={aamarId}
            onCustomerSelect={setCustomerId}
          />
        </div>

        <div className="d-flex gap-3 justify-content-center align-items-center text-center">
          <button
            className="btn btn-dark w-100 mt-3"
            style={{ borderRadius: "5px" }}
            onClick={handleExportCSV}
          >
            <Icons.DownloadOutline size={22} /> Download CSV
          </button>

          <button
            className="btn btn-dark w-100 mt-3"
            style={{ borderRadius: "5px" }}
            onClick={handleExportPDF}
          >
            <Icons.DocumentOutline size={22} /> Download PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerWiseSalesReturnReport;
